import { buildApiParams } from '@/lib/filterRegistry';
import type { FilterConfig, FilterNegations, FilterValue } from '@/types/filters.types';
import type {
  ConsultListParams,
  ConsultRowStatus,
  ConsultSearchType,
} from '@/services/consults.service';
import { getErrorMessage } from '@/services/apiClient';

/**
 * Every sortable column key on the Consults worklist, in header order.
 *
 * These are a CONTRACT WITH THE SERVER. `DataTable` sends a column's `key`
 * verbatim as the sort key, and ConsultsListService::sortColumn() falls back to
 * the default for anything it does not recognise, so a rename on either side
 * does not error: the column silently stops sorting while its header keeps
 * showing an arrow.
 *
 * consultsSortKeys.test.ts reads the PHP service and asserts the two lists
 * still match, which is the only check that spans the seam.
 */
export const CONSULTS_SORT_KEYS = [
  'consultDate',
  'planDisplayId',
  'patientName',
  'facilityName',
  'procedures',
  'picsIn',
  'planType',
  'coordinatorName',
  'stage',
  'doctorName',
  'taskCount',
  'due',
  'inquiryStatus',
  'interest',
] as const;

export type ConsultsSortKey = (typeof CONSULTS_SORT_KEYS)[number];

/**
 * The window the list opens on: the last 365 days through today, legacy's
 * own default when no date was picked (inquiry_requests_db.php:1005-1008).
 * Future consults appear once the user picks a range that includes them.
 */
export const CONSULTS_DEFAULT_DATE_PRESET = '365days' as const;

/**
 * The Consults worklist's filter sidebar, in legacy's order: Consult Date,
 * Doctor, Coordinator, Facility, Plan Type, Status, Stages.
 *
 * Keys mirror the legacy filter types, and the option lists are the Plans
 * list's (usePlanFilterOptions). Do not rename a key: saved lists store them.
 */
export function buildConsultsFilterConfigs(
  doctorLabel: string,
  coordLabel: string,
): FilterConfig[] {
  return [
    { key: 'search', label: 'Search', type: 'search', allLabel: 'Search consults...' },
    {
      key: 'consult-daterange',
      label: 'Consult Date',
      type: 'daterange',
      defaultPreset: CONSULTS_DEFAULT_DATE_PRESET,
    },
    {
      key: 'doctor',
      label: doctorLabel,
      type: 'select',
      allLabel: `All ${doctorLabel.toLowerCase()}s`,
      multi: true,
    },
    {
      key: 'coordinator',
      label: coordLabel,
      type: 'select',
      allLabel: `All ${coordLabel.toLowerCase()}s`,
      multi: true,
    },
    { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
    { key: 'plan-type', label: 'Plan Type', type: 'select', allLabel: 'All plan types', multi: true },
    { key: 'plan-status', label: 'Status', type: 'select', allLabel: 'All statuses', multi: true },
    { key: 'plan-stages', label: 'Stages', type: 'select', allLabel: 'All stages', multi: true },
  ];
}

/** The sidebar's filter keys. Labels vary by account; keys do not. */
const CONSULTS_FILTER_KEYS: ReadonlySet<string> = new Set(
  buildConsultsFilterConfigs('Doctor', 'Coordinator').map((c) => c.key),
);

/** Paging, sort and search mode — everything the page sends besides filters. */
export interface ConsultsListBase {
  page: number;
  perPage: number;
  sort: string;
  order: 'asc' | 'desc';
  searchType: ConsultSearchType;
}

/**
 * The parameters the Consults worklist sends to GET /consults and, unchanged,
 * to the export. Built on the shared `buildApiParams`, and restricted to the
 * sidebar's own keys so a value another list left in browser storage cannot
 * ride along.
 *
 * The server's half of this contract is ConsultsListService::FILTER_KEYS;
 * consultsFilterKeys.test.ts reads it to prove the two agree.
 */
export function buildConsultsListParams(
  values: Record<string, FilterValue>,
  negations: FilterNegations,
  base: ConsultsListBase,
): ConsultListParams {
  const ownValues: Record<string, FilterValue> = {};
  for (const [key, value] of Object.entries(values)) {
    if (CONSULTS_FILTER_KEYS.has(key)) ownValues[key] = value;
  }

  const { filtersArray, search } = buildApiParams(ownValues, negations);

  return search ? { ...base, filtersArray, search } : { ...base, filtersArray };
}

/**
 * True when the request narrows the list beyond its default window: a search
 * term, or any filter other than the consult date.
 */
export function hasActiveConsultsFilters(params: ConsultListParams): boolean {
  if (params.search) return true;
  try {
    const sent = JSON.parse(params.filtersArray || '{}') as Record<string, unknown>;
    return Object.keys(sent).some((key) => key !== 'consult-daterange');
  } catch {
    return false;
  }
}

/**
 * Row background per `rowStatus`, standing in for legacy's green / yellow /
 * pink rows. Tints match the Surgery worklist's.
 */
export const CONSULT_ROW_STATUS_STYLES: Record<ConsultRowStatus, string> = {
  signedUp: 'bg-emerald-50',
  pastDue: 'bg-amber-50',
  cancelled: 'bg-red-50',
  none: '',
};

/** Legacy draws three stars, filled while the interest level reaches each one. */
export const INTEREST_STAR_COUNT = 3;

export function filledStars(interest: number): number {
  if (!Number.isFinite(interest) || interest <= 0) return 0;
  return Math.min(INTEREST_STAR_COUNT, Math.floor(interest));
}

/** "Sep 15, 2026" → ["Sep 15", "2026"], for the two-line date the Inquiries list uses. */
export function splitDisplayDate(formatted: string): [string, string] | null {
  const match = formatted.match(/^(.+), (\d{4})$/);
  return match ? [match[1], match[2]] : null;
}

/** What the worklist body shows in place of, or inside, the table. */
export type ConsultsListView =
  | { kind: 'refused'; title: string; message: string }
  | { kind: 'failed'; title: string; message: string }
  | { kind: 'table'; emptyTitle: string; emptyDescription: string };

export const CONSULTS_RETRY_OR_SUPPORT = 'Try again. If it keeps happening, contact Prospera support.';

/** Fallback when a refusal arrives without a readable server message. */
export const CONSULTS_REFUSED_FALLBACK =
  'You do not have access to Consults. Ask your administrator to give you Consult Portal access.';

function responseStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const status = (error as { response?: { status?: unknown } }).response?.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Decides what the worklist body shows. A failed request never falls through
 * to the empty state: "no consults" would be false when the list did not load.
 */
export function consultsListView(state: {
  isError: boolean;
  error: unknown;
  hasActiveFilters: boolean;
}): ConsultsListView {
  if (state.isError) {
    if (responseStatus(state.error) === 403) {
      return {
        kind: 'refused',
        title: "Consults isn't available",
        message: getErrorMessage(state.error, CONSULTS_REFUSED_FALLBACK),
      };
    }
    return {
      kind: 'failed',
      title: 'Could not load consults',
      message: CONSULTS_RETRY_OR_SUPPORT,
    };
  }

  return state.hasActiveFilters
    ? {
        kind: 'table',
        emptyTitle: 'No consults match these filters',
        emptyDescription: 'Change or clear the filters to see more consults.',
      }
    : {
        kind: 'table',
        emptyTitle: 'No consults in this date range',
        emptyDescription: 'Pick a different Consult Date range to see more.',
      };
}
